import { useEffect, useState } from "react";
import { PageHeader, Empty } from "@/components/UIBits";
import api from "@/lib/api";
import { Copy, Check, Trash2, Plus, Loader2 } from "lucide-react";

export default function InviteCodes() {
  const [codes, setCodes] = useState([]);
  const [genCount, setGenCount] = useState(5);
  const [loading, setLoading] = useState(false);
  const [copiedId, setCopiedId] = useState(null);

  const load = async () => {
    try {
      setCodes(await api.listInvites());
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => { load(); }, []);

  const generate = async () => {
    setLoading(true);
    try {
      await api.generateInvites(genCount);
      await load();
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.deleteInvite(id);
      await load();
    } catch (e) {
      console.error(e);
    }
  };

  const copy = (code, id) => {
    navigator.clipboard.writeText(code);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 1500);
  };

  const unused = codes.filter(c => !c.is_used);
  const used = codes.filter(c => c.is_used);

  return (
    <div data-testid="invite-codes-page">
      <PageHeader
        title="Invite Codes"
        subtitle="// Beta access management"
        testid="invite-header"
        actions={
          <div className="flex items-center gap-2">
            <select
              value={genCount}
              onChange={(e) => setGenCount(Number(e.target.value))}
              className="bg-[#0e0e10] border border-[#27272a] text-white font-mono text-xs px-2 py-2"
              data-testid="invite-count-select"
            >
              {[1, 5, 10, 25].map(n => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
            <button
              onClick={generate}
              disabled={loading}
              className="flex items-center gap-1.5 bg-white text-black font-mono text-xs px-4 py-2 hover:bg-zinc-200 transition-colors disabled:opacity-50"
              data-testid="generate-invites-btn"
            >
              {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
              Generate
            </button>
          </div>
        }
      />

      {/* Unused codes */}
      <h2 className="font-heading text-lg text-white mb-3">Available ({unused.length})</h2>
      {unused.length === 0 ? (
        <Empty testid="no-unused-codes">No unused invite codes — generate some above</Empty>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2 mb-10">
          {unused.map(c => (
            <div
              key={c.id}
              className="border border-[#1f1f23] bg-[#0e0e10] p-3 flex items-center justify-between"
            >
              <code className="font-mono text-sm text-emerald-400" data-testid={`code-${c.id}`}>
                {c.code}
              </code>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => copy(c.code, c.id)}
                  className="text-zinc-500 hover:text-white p-1 transition-colors"
                  data-testid={`copy-${c.id}`}
                >
                  {copiedId === c.id ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                </button>
                <button
                  onClick={() => remove(c.id)}
                  className="text-zinc-500 hover:text-red-400 p-1 transition-colors"
                  data-testid={`delete-${c.id}`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Used codes */}
      {used.length > 0 && (
        <>
          <h2 className="font-heading text-lg text-white mb-3">Used ({used.length})</h2>
          <div className="space-y-1">
            {used.map(c => (
              <div
                key={c.id}
                className="border border-[#1f1f23] bg-[#0a0a0c] p-3 flex items-center justify-between opacity-60"
              >
                <code className="font-mono text-sm text-zinc-500 line-through">{c.code}</code>
                <span className="font-mono text-[10px] text-zinc-600">
                  used by {c.used_by} · {new Date(c.used_at).toLocaleDateString()}
                </span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
